/** Action vocabulary for the watermark library (VE-24693). */
import { createAction } from '@reduxjs/toolkit';
import {
  createGraphQLFailureAction,
  createGraphQLSuccessAction,
} from '@helpers/callGraphQLApi';
import {
  CreateWatermarkResponse,
  DeleteWatermarkResponse,
  FetchWatermarksResponse,
  WatermarkDraft,
} from '../models';

export const FETCH_WATERMARKS_REQUEST = createAction<{
  limit: number;
  offset: number;
  /**
   * Ask the server for only what a PICKER may offer — live and enabled.
   *
   * For the editor and folder routes, which consume the library exclusively
   * through `selectPickableWatermarks` and so would discard these rows anyway;
   * it saves downloading a deleted watermark's artwork to throw it away.
   *
   * Never set for the settings page. That page needs the rows a picker must not
   * see — the deleted view, and disabled rows, which are only re-enableable
   * from there — and it is the only surface that WRITES: `findRecord` feeds the
   * stored record into a whole-document replace, so a partial list there would
   * erase whatever the filter left out.
   */
  pickableOnly?: boolean;
}>('WATERMARKS/FETCH_WATERMARKS_REQUEST');
export const FETCH_WATERMARKS_SUCCESS =
  createGraphQLSuccessAction<FetchWatermarksResponse>(
    'WATERMARKS/FETCH_WATERMARKS_SUCCESS'
  );
export const FETCH_WATERMARKS_FAILURE = createGraphQLFailureAction(
  'WATERMARKS/FETCH_WATERMARKS_FAILURE'
);

export const CREATE_WATERMARK_REQUEST = createAction<{
  /** A DRAFT: it may still carry the upload's own bytes on their way to the TDO. */
  data: WatermarkDraft;
}>('WATERMARKS/CREATE_WATERMARK_REQUEST');
export const CREATE_WATERMARK_SUCCESS =
  createGraphQLSuccessAction<CreateWatermarkResponse>(
    'WATERMARKS/CREATE_WATERMARK_SUCCESS'
  );
export const CREATE_WATERMARK_FAILURE = createGraphQLFailureAction(
  'WATERMARKS/CREATE_WATERMARK_FAILURE'
);

export const UPDATE_WATERMARK_REQUEST = createAction<{
  watermark: { id: string; data: WatermarkDraft };
}>('WATERMARKS/UPDATE_WATERMARK_REQUEST');
export const UPDATE_WATERMARK_SUCCESS =
  createGraphQLSuccessAction<CreateWatermarkResponse>(
    'WATERMARKS/UPDATE_WATERMARK_SUCCESS'
  );
export const UPDATE_WATERMARK_FAILURE = createGraphQLFailureAction(
  'WATERMARKS/UPDATE_WATERMARK_FAILURE'
);

export const DELETE_WATERMARK_REQUEST = createAction<{ id: string }>(
  'WATERMARKS/DELETE_WATERMARK_REQUEST'
);
export const DELETE_WATERMARK_SUCCESS =
  createGraphQLSuccessAction<DeleteWatermarkResponse>(
    'WATERMARKS/DELETE_WATERMARK_SUCCESS'
  );
export const DELETE_WATERMARK_FAILURE = createGraphQLFailureAction(
  'WATERMARKS/DELETE_WATERMARK_FAILURE'
);

/**
 * Apply a library watermark to the open document (§K).
 *
 * Exists because applying one may now require a FETCH: since the artwork moved
 * to the watermark's own TDO, a picker holds only the small preview, and the
 * full raster has to be resolved before the mark can be drawn. That is a side
 * effect, so the picker asks for the mark and a saga produces the
 * `ADD_WATERMARK` once it has something to draw with.
 *
 * `instanceId` is minted by the caller, as it is everywhere else, so the
 * dispatching component keeps its usual control over instance identity.
 */
export const APPLY_LIBRARY_WATERMARK = createAction<{
  instanceId: string;
  definitionId: string;
  /**
   * Add it to THIS FILE ONLY rather than to the merged document this file
   * belongs to (VE-30104). Absent means the set, which is the default and the
   * common case.
   *
   * CARRIED ON THE REQUEST, because this saga resolves artwork before it
   * dispatches the ADD. Setting the scope separately at the call site raced
   * it: the mark did not exist yet, the scope change found nothing to move,
   * and the ADD that landed afterwards took the default.
   */
  fileOnly?: boolean;
}>('WATERMARKS/APPLY_LIBRARY_WATERMARK');

/**
 * Marks a definition as mid-apply, or done (VE-32397). Put only by the apply
 * saga, around the artwork fetch.
 */
export const SET_WATERMARK_APPLYING = createAction<{
  definitionId: string;
  applying: boolean;
}>('WATERMARKS/SET_WATERMARK_APPLYING');

// --- UI-only actions ---
export const OPEN_WATERMARK_DRAWER = createAction<{ editingId?: string }>(
  'WATERMARKS/OPEN_WATERMARK_DRAWER'
);
export const CLOSE_WATERMARK_DRAWER = createAction(
  'WATERMARKS/CLOSE_WATERMARK_DRAWER'
);
/**
 * Open (or close) the delete confirmation for a row.
 *
 * `permanent` says WHICH delete is being confirmed. Both go through one dialog
 * because they are the same question asked about different stakes, and the
 * dialog is where the difference gets spelled out — the permanent one is the
 * only irreversible action in this feature and it used to fire straight from a
 * menu click, with the recoverable soft delete getting the confirmation instead.
 */
export const SET_DELETING_WATERMARK = createAction<{
  id?: string;
  permanent?: boolean;
}>('WATERMARKS/SET_DELETING_WATERMARK');

// --- convenience creators (match the settingsProfile ergonomics) ---
export const actionFetchWatermarks = (payload: {
  limit: number;
  offset: number;
  pickableOnly?: boolean;
}) => FETCH_WATERMARKS_REQUEST(payload);
export const actionCreateWatermark = (payload: { data: WatermarkDraft }) =>
  CREATE_WATERMARK_REQUEST(payload);
export const actionUpdateWatermark = (payload: {
  watermark: { id: string; data: WatermarkDraft };
}) => UPDATE_WATERMARK_REQUEST(payload);
export const actionDeleteWatermark = (payload: { id: string }) =>
  DELETE_WATERMARK_REQUEST(payload);

/**
 * Whether the watermark registry has a published schema on this cluster.
 *
 * Carries the value rather than asserting the bad one, because it has to be
 * able to go BACK. The previous version could only latch "unavailable" and
 * nothing cleared it, so one 502 during boot mislabelled a healthy cluster for
 * the whole session and sent every write to browser-local storage — announced
 * to the user as "shared storage is not set up for your organization".
 *
 * Set only from a RESOLVED lookup, and only for `unprovisioned`. A request that
 * merely failed says nothing about the cluster and must not set this.
 */
export const SET_WATERMARK_REGISTRY_UNAVAILABLE = createAction<{
  unavailable: boolean;
}>('redact/watermarks/SET_WATERMARK_REGISTRY_UNAVAILABLE');

/**
 * Restore a soft-deleted watermark: clears `isDeleted` and puts the row last.
 * Reuses the UPDATE success/failure pair, so the toast and refetch flow are
 * shared with an ordinary save.
 */
export const RESTORE_WATERMARK_REQUEST = createAction<{ id: string }>(
  'redact/watermarks/RESTORE_WATERMARK_REQUEST'
);

/**
 * Remove a soft-deleted watermark's SDO for good.
 *
 * Separate from DELETE_WATERMARK_REQUEST, which is now the SOFT delete —
 * keeping one action for both would mean the destructive path was one
 * mis-passed flag away from the everyday one. Reuses the DELETE success/failure
 * pair because from the store's point of view the row leaving is the same event.
 */
export const PERMANENTLY_DELETE_WATERMARK_REQUEST = createAction<{
  id: string;
}>('redact/watermarks/PERMANENTLY_DELETE_WATERMARK_REQUEST');

/** Toggle the settings page between the live library and the deleted view. */
export const SET_SHOW_DELETED_WATERMARKS = createAction<{ show: boolean }>(
  'redact/watermarks/SET_SHOW_DELETED_WATERMARKS'
);

/**
 * Show or hide a watermark in the pickers without deleting it.
 *
 * A save like any other — it goes through the same upsert and reuses the UPDATE
 * success/failure pair — but it gets its own action because the row toggle is
 * the only caller and passing a whole definition through the UI to flip one
 * boolean is how a toggle ends up clobbering a field it never meant to touch.
 */
export const SET_WATERMARK_ENABLED = createAction<{
  id: string;
  enabled: boolean;
}>('redact/watermarks/SET_WATERMARK_ENABLED');

/**
 * Flip one watermark's Auto Apply from the settings table.
 *
 * The same shape as {@link SET_WATERMARK_ENABLED} and for the same reason: the
 * row toggle is the only caller, and passing a whole definition through the UI
 * to change one boolean is how a toggle clobbers a field it never meant to
 * touch.
 */
export const SET_WATERMARK_AUTO_APPLY = createAction<{
  id: string;
  autoApply: boolean;
}>('redact/watermarks/SET_WATERMARK_AUTO_APPLY');
